import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ScanLine, Keyboard, Camera, Check } from "lucide-react";

// Attendance scanner: phone/laptop camera QR scanning + USB barcode/RFID
// reader (which types the code + Enter into the focused box). Both call
// POST /attendance/scan which resolves rfid_uid OR student_id and marks present.
export default function AttendanceScanner({ onClose, onMarked }) {
  const [mode, setMode] = useState("camera"); // camera | manual
  const [log, setLog] = useState([]);         // recent scans
  const [typed, setTyped] = useState("");
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", t: 0 });
  const inputRef = useRef(null);

  const submit = async (payload) => {
    const code = String(payload || "").trim();
    if (!code) return;
    const now = Date.now();
    if (lastRef.current.code === code && now - lastRef.current.t < 2500) return; // debounce
    lastRef.current = { code, t: now };
    try {
      const r = await api.post("/attendance/scan", { payload: code });
      const stu = r.data.student;
      setLog((l) => [{ ok: true, name: stu.name, cls: stu.class_section,
        already: r.data.already_present, t: now }, ...l].slice(0, 12));
      toast.success(`${stu.name} · ${r.data.already_present ? "already present" : "marked present"}`);
      onMarked && onMarked();
    } catch (e) {
      setLog((l) => [{ ok: false, name: e?.response?.data?.detail || "No match", t: now }, ...l].slice(0, 12));
      toast.error(e?.response?.data?.detail || "Scan failed");
    }
  };

  useEffect(() => {
    if (mode !== "camera") return;
    let cancelled = false;
    const el = "att-qr-reader";
    const scanner = new Html5Qrcode(el, { verbose: false });
    scannerRef.current = scanner;
    // Track the start promise so cleanup never clears a reader mid-start.
    const startPromise = Html5Qrcode.getCameras().then((cams) => {
      if (cancelled || !cams || !cams.length) {
        if (!cancelled) toast.error("No camera found — use USB/manual tab");
        return;
      }
      const camId = cams[cams.length - 1].id; // prefer back camera
      return scanner.start(camId, { fps: 10, qrbox: { width: 230, height: 230 } },
        (decoded) => submit(decoded), () => {});
    }).catch(() => { if (!cancelled) toast.error("Camera unavailable — use USB/manual tab"); });

    return () => {
      cancelled = true;
      Promise.resolve(startPromise).then(() => {
        try {
          if (scanner.getState && scanner.getState() === 2 /* SCANNING */) {
            return scanner.stop();
          }
        } catch { /* noop */ }
      }).catch(() => {}).finally(() => { try { scanner.clear(); } catch { /* noop */ } });
    };
  }, [mode]); // eslint-disable-line

  useEffect(() => { if (mode === "manual" && inputRef.current) inputRef.current.focus(); }, [mode]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="att-scanner-modal">
      <div className="nb-card bg-white w-full max-w-lg p-5 space-y-3 max-h-[94vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="display text-lg font-bold flex items-center gap-2"><ScanLine size={18}/>Scan Attendance</h3>
          <button onClick={onClose} className="nb-btn nb-btn-ghost text-xs" data-testid="att-scanner-close"><X size={14}/></button>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>setMode("camera")} className={`nb-btn text-xs ${mode==="camera"?"":"nb-btn-ghost"}`} data-testid="att-scan-mode-camera"><Camera size={14}/>Phone Camera (QR)</button>
          <button onClick={()=>setMode("manual")} className={`nb-btn text-xs ${mode==="manual"?"":"nb-btn-ghost"}`} data-testid="att-scan-mode-manual"><Keyboard size={14}/>USB Scanner / Manual</button>
        </div>

        {mode === "camera" ? (
          <div>
            <div id="att-qr-reader" className="w-full rounded-lg overflow-hidden border-2 border-black" data-testid="att-qr-reader"/>
            <p className="text-xs text-slate-600 mt-2">Point the camera at a card's QR code. Each valid scan marks that student present for today.</p>
          </div>
        ) : (
          <form onSubmit={(e)=>{e.preventDefault(); submit(typed); setTyped("");}} className="space-y-2">
            <label className="nb-label">Scan or type card code / RFID UID</label>
            <input ref={inputRef} className="nb-input mono" value={typed} autoFocus
                   onChange={(e)=>setTyped(e.target.value)}
                   placeholder="Tap a USB reader here, or type and press Enter"
                   data-testid="att-scan-input"/>
            <button className="nb-btn w-full justify-center" data-testid="att-scan-submit"><Check size={16}/>Mark Present</button>
            <p className="text-xs text-slate-600">USB barcode/RFID readers act like a keyboard — just keep this box focused and tap a card.</p>
          </form>
        )}

        <div className="border-t-2 border-black pt-2">
          <div className="text-xs font-bold mb-1">Recent ({log.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="att-scan-log">
            {log.map((e, i) => (
              <div key={i} className={`text-xs flex items-center gap-2 ${e.ok?"text-emerald-700":"text-rose-700"}`}>
                {e.ok ? <Check size={12}/> : <X size={12}/>}
                <span className="font-bold">{e.name}</span>
                {e.ok && <span className="text-slate-500">{e.cls}{e.already?" · already present":""}</span>}
              </div>
            ))}
            {log.length===0 && <div className="text-xs text-slate-400">No scans yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
